import { buildRingLayoutPositions } from './graphLayoutUtils';
import { normalizeGraphPayload, mergeNormalizedGraphParts } from './graphDataTransform';

export const calculateExpandedNodePosition = ({
  clickedPosition,
  parentPosition,
  graphRingBaseRadius,
  expansionNodeScaleFactor,
  expansionNodeMaxDistance,
}) => {
  if (parentPosition) {
    const deltaX = clickedPosition.x - parentPosition.x;
    const deltaY = clickedPosition.y - parentPosition.y;
    const distanceToParent = Math.hypot(deltaX, deltaY);
    const safeDistance = distanceToParent > 0 ? distanceToParent : graphRingBaseRadius;
    const scaledDistance = Math.min(safeDistance * expansionNodeScaleFactor, expansionNodeMaxDistance);
    const scaleFactor = scaledDistance / safeDistance;

    return {
      x: parentPosition.x + (deltaX * scaleFactor),
      y: parentPosition.y + (deltaY * scaleFactor),
    };
  }

  const clickedRadius = Math.sqrt((clickedPosition.x * clickedPosition.x) + (clickedPosition.y * clickedPosition.y));
  const outwardAngle = Math.atan2(clickedPosition.y, clickedPosition.x);
  const targetRadius = Math.min(clickedRadius * expansionNodeScaleFactor, expansionNodeMaxDistance);

  return {
    x: Math.cos(outwardAngle) * targetRadius,
    y: Math.sin(outwardAngle) * targetRadius,
  };
};

export const buildExpansionAppendMutation = ({
  expandedData,
  anchorNodeId,
  currentCenterNodeId,
  destinyNodeId,
  existingNodeIds = [],
  expansionRingBaseRadius,
  expansionRingBuffer,
  expansionRingMinSpacing,
}) => {
  const normalizedExpansion = normalizeGraphPayload({
    dataSource: expandedData,
    currentCenterNodeId,
    destinyNodeId,
  });

  const existingNodeIdSet = new Set(existingNodeIds);
  const branchNodeIds = (normalizedExpansion.nodes || [])
    .map((node) => node?.data?.id)
    .filter((nodeId) => nodeId && nodeId !== anchorNodeId && !existingNodeIdSet.has(nodeId));

  const branchElements = mergeNormalizedGraphParts([
    normalizeGraphPayload({
      dataSource: expandedData,
      currentCenterNodeId: anchorNodeId,
      destinyNodeId,
    }),
  ], anchorNodeId)
    .filter((element) => element?.data && !element.data.source && !element.data.target)
    .filter((element) => {
      const nodeId = element?.data?.id;
      return nodeId && nodeId !== anchorNodeId && branchNodeIds.includes(nodeId);
    });

  const branchRelativePositions = Object.fromEntries(
    Array.from(
      buildRingLayoutPositions(
        branchElements,
        expansionRingBaseRadius + expansionRingBuffer,
        expansionRingMinSpacing
      ).entries()
    )
  );

  return {
    branchNodeIds,
    mutation: {
      type: 'append',
      anchorNodeId,
      branchNodeIds,
      branchRelativePositions,
    },
  };
};
